import React from "react";
import { CSVLink } from "react-csv";
import clsx from "clsx";

import { ReactComponent as DownloadIcon } from "../../../assets/svg/download.svg";
import csvSheetImg from "../../../assets/images/csv_sheet.png";
import Spinner from "../../../atoms/Spinner";
import Sidebar from "../../../organisms/teacher/Sidebar";
import { ITeachingMaterial, ISimpleTeachingMaterial } from "../../../state";
import { CategoryType } from "../../../constants/teaching_materials";
import useCategorizeMaterials from "../../../hooks/useCategorizeMaterials";
import {
  getCurrentMaterials,
  resolveActiveTab,
} from "../../../libs/teachingMaterialUtils";
import { BA_WORK_SELECTION_LESSON_ID } from "../../../pages/teacher/DownloadCSVPage";

import styles from "./DownloadCSV.module.scss";

export type Props = {
  calling: boolean;
  sidebarMaterials: ITeachingMaterial[];
  lessonMaterials: ISimpleTeachingMaterial[];
  dataCSV: string[][];
  goBack: () => void;
  onDownLoadData: (
    teachingMaterialId: number,
    lessonId: number,
    roundNo: number,
  ) => void;
};

export const DownloadCSV: React.FC<Props> = ({
  calling,
  sidebarMaterials,
  lessonMaterials,
  dataCSV,
  goBack,
  onDownLoadData,
}) => {
  const [selectedMaterialId, setSelectedMaterialId] = React.useState<
    number | null
  >(null);
  const [pendingLesson, setPendingLesson] = React.useState<{
    lessonId: number;
    roundNo: number;
  } | null>(null);
  const [manualTab, setManualTab] = React.useState<CategoryType | null>(null);

  const mountedRef = React.useRef(false);
  const [hasLoaded, setHasLoaded] = React.useState(false);
  const linkCSV = React.useRef<
    CSVLink & HTMLAnchorElement & { link?: HTMLAnchorElement }
  >(null);

  React.useEffect(() => {
    if (mountedRef.current) {
      setHasLoaded(true);
    }
    mountedRef.current = true;
  }, [lessonMaterials]);

  const categorizedMaterials = useCategorizeMaterials(sidebarMaterials);

  const activeTab = React.useMemo(
    () => resolveActiveTab(categorizedMaterials, manualTab),
    [categorizedMaterials, manualTab],
  );

  const filteredSidebarMaterials = React.useMemo(
    () => getCurrentMaterials(categorizedMaterials, activeTab),
    [categorizedMaterials, activeTab],
  );

  React.useEffect(() => {
    if (filteredSidebarMaterials.length > 0) {
      setSelectedMaterialId(filteredSidebarMaterials[0].id);
    }
  }, [filteredSidebarMaterials]);

  const selectedSidebarMaterial = React.useMemo(
    () => sidebarMaterials.find(m => m.id === selectedMaterialId) ?? null,
    [sidebarMaterials, selectedMaterialId],
  );

  const selectedLessonMaterial = React.useMemo(
    () => lessonMaterials.find(m => m.id === selectedMaterialId) ?? null,
    [lessonMaterials, selectedMaterialId],
  );

  const lessonItems = React.useMemo(() => {
    if (!selectedLessonMaterial) return [];
    const lessons = selectedLessonMaterial.lessons;
    const items: { id: number; label: string; maxRoundNo: number }[] = [];

    if (lessons.some(l => l.one_page_portfolio_assessment_id)) {
      items.push({
        id: BA_WORK_SELECTION_LESSON_ID,
        label: "Before/After WORK",
        maxRoundNo: 1,
      });
    }

    lessons
      .filter(l => l.name.trim() !== "" || l.is_leaf)
      .forEach(l => {
        const label = l.is_leaf ? "実践ワークシート" : `${l.name}  ${l.title}`;
        items.push({ id: l.id, label, maxRoundNo: l.max_round_no });
      });

    return items;
  }, [selectedLessonMaterial]);

  React.useEffect(() => {
    if (linkCSV.current && pendingLesson !== null && dataCSV.length > 0) {
      setTimeout(() => {
        linkCSV.current?.link?.click();
      });
      setPendingLesson(null);
    }
  }, [dataCSV, pendingLesson]);

  const handleDownload = React.useCallback(
    (lessonId: number, roundNo: number) => {
      if (!selectedLessonMaterial) return;
      setPendingLesson({ lessonId, roundNo });
      onDownLoadData(selectedLessonMaterial.id, lessonId, roundNo);
    },
    [selectedLessonMaterial, onDownLoadData],
  );

  return (
    <div className={styles.pageLayout}>
      {!hasLoaded ? (
        <div className={styles.loading}>
          <Spinner spinColor="black" />
          <p className={styles.loadingText}>教材取得中...</p>
        </div>
      ) : (
        <>
          <Sidebar
            classList={[]}
            currentMaterials={filteredSidebarMaterials}
            viewingMaterialId={selectedMaterialId}
            activeModalId={null}
            activeTab={activeTab}
            targetClassIds={[]}
            showTabs={categorizedMaterials.hasMultipleMaterialCategories}
            hasMiddle={categorizedMaterials.hasMiddle}
            hasHigh={categorizedMaterials.hasHigh}
            hasCore={categorizedMaterials.hasCore}
            hasBase={categorizedMaterials.hasBase}
            backLabel="データ選択に戻る"
            onBackToClass={goBack}
            onTabClick={tab => setManualTab(tab)}
            onItemClick={(_, materialId) => setSelectedMaterialId(materialId)}
          />

          <div className={styles.main}>
            <div className={styles.innerContent}>
              <h2 className={styles.pageTitle}>回答のCSVデータ</h2>

              <div className={styles.imageCard}>
                <img
                  src={csvSheetImg}
                  alt="回答のCSVデータ"
                  className={styles.csvImage}
                />
                <p className={styles.imageCardDesc}>
                  生徒データを細かく分析したい先生向けのCSVデータです。
                </p>
              </div>

              {selectedSidebarMaterial && (
                <>
                  <h3 className={styles.materialTitle}>
                    {selectedSidebarMaterial.name.trim()}{" "}
                    {selectedSidebarMaterial.title.replace(
                      /\s*-高校-|\s*-中学-/g,
                      "",
                    )}
                  </h3>

                  <div className={styles.lessonList}>
                    {lessonItems.map(item => {
                      const roundNos = Array.from(
                        { length: item.maxRoundNo },
                        (_, index) => index + 1,
                      );
                      return roundNos.map(roundNo => (
                        <div
                          key={`${item.id}-${roundNo}`}
                          className={clsx(
                            styles.lessonItem,
                            calling && styles.disabled,
                          )}
                          onClick={() => handleDownload(item.id, roundNo)}
                        >
                          <div className={styles.downloadIconArea}>
                            <DownloadIcon className={styles.downloadIcon} />
                          </div>
                          <div className={styles.lessonTextArea}>
                            <span className={styles.lessonLabel}>
                              {item.maxRoundNo > 1
                                ? `${item.label} (${roundNo})`
                                : item.label}
                            </span>
                          </div>
                        </div>
                      ));
                    })}
                  </div>
                </>
              )}

              <CSVLink
                filename="download.csv"
                data={dataCSV}
                uFEFF={true}
                hidden={true}
                ref={linkCSV}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default DownloadCSV;
